// ─── PERK CARD SYSTEM v1 (BETA 22.87) ────────────────────────────────────────
// Dylan's card model, locked in:
//   Perk FAMILY  = what the card is        (26 families, one per card art)
//   LEVEL        = how many times you've taken that family (LV badge, right plate)
//   RARITY       = how strong THIS roll is (frame color + text above the card)
//   EFFECT       = one simple buff only    (bottom box — never multiple buffs)
//
// The 26 families are the player-facing identity of the perc system. Underneath,
// each family maps 1:1 onto an existing perc id (T##/N##) so runtime slots,
// telemetry (percLevels keys), the Balance Charter, and the bot pipeline keep
// their history. Mapping source of truth: Dylan's 01_CARD_TRACKER.txt.
//
// Card art: /perk-cards/{common|rare|epic|legendary}/{file}.webp — 104 baked
// frames (art + tier shell + inner shadow), text drawn LIVE by the engine into
// the card's own plates. NEVER bake text into the art (evergreen rule).

import type { UpgradeRarity } from './types';
import type { PeanutPerc } from './peanutPercs';
import { rollBuffJitter, luckValueMult } from './peanutPercs';
import { rollRng } from './rng';

export type CardCategory =
  | 'SURVIVAL' | 'DAMAGE' | 'UTILITY' | 'WEAPON' | 'PROJECTILE' | 'SUMMON' | 'ECONOMY';

export interface PerkCardFamily {
  /** underlying perc id (runtime slots + telemetry keep these keys) */
  percId: string;
  /** card art file base, e.g. '01_protection' → /perk-cards/{tier}/01_protection.webp */
  file: string;
  /** player-facing family name (top plate, ALL CAPS) */
  display: string;
  /** bottom small plate */
  category: CardCategory;
  /** LV 1 unlock text (bottom box). LV 2+ uses the rolled one-buff line. */
  unlockText: string;
}

// Dylan's 01_CARD_TRACKER.txt mapping (card → effect), joined to the perc roster.
export const PERK_CARD_FAMILIES: PerkCardFamily[] = [
  { percId: 'T11', file: '01_protection',        display: 'PROTECTION',        category: 'SURVIVAL',   unlockText: 'Gain a protective shield. Blocks incoming damage.' },
  { percId: 'T01', file: '02_heavy_petting',     display: 'HEAVY PETTING',     category: 'DAMAGE',     unlockText: 'All damage dealt is increased.' },
  { percId: 'T02', file: '03_fast_hands',        display: 'FAST HANDS',        category: 'DAMAGE',     unlockText: 'Your weapons fire faster.' },
  { percId: 'T03', file: '04_thick_meat',        display: 'THICK MEAT',        category: 'SURVIVAL',   unlockText: 'Increases your max HP.' },
  { percId: 'T04', file: '05_quick_finish',      display: 'QUICK FINISH',      category: 'UTILITY',    unlockText: 'You move faster.' },
  { percId: 'T05', file: '06_slippery_when_wet', display: 'SLIPPERY WHEN WET', category: 'SURVIVAL',   unlockText: 'Chance to dodge incoming hits.' },
  { percId: 'T06', file: '07_lucky_stroke',      display: 'LUCKY STROKE',      category: 'DAMAGE',     unlockText: 'Chance to land critical hits.' },
  { percId: 'N04', file: '08_deep_impact',       display: 'DEEP IMPACT',       category: 'DAMAGE',     unlockText: 'Critical hits deal more damage.' },
  { percId: 'T07', file: '09_gold_digger',       display: 'GOLD DIGGER',       category: 'ECONOMY',    unlockText: 'Earn more credits from everything.' },
  { percId: 'T12', file: '10_fast_learner',      display: 'FAST LEARNER',      category: 'UTILITY',    unlockText: 'Gain more XP from kills.' },
  { percId: 'N09', file: '11_reach_around',      display: 'REACH AROUND',      category: 'UTILITY',    unlockText: 'Pick up loot from further away.' },
  { percId: 'N02', file: '12_safe_word',         display: 'SAFE WORD',         category: 'SURVIVAL',   unlockText: 'Take reduced damage from all hits.' },
  { percId: 'N01', file: '13_suck_tax',          display: 'SUCK TAX',          category: 'SURVIVAL',   unlockText: 'Heal a cut of the damage you deal.' },
  { percId: 'N03', file: '14_touch_me_not',      display: 'TOUCH ME NOT',      category: 'DAMAGE',     unlockText: 'Reflect damage back when hit.' },
  { percId: 'T08', file: '15_back_shots',        display: 'BACK SHOTS',        category: 'UTILITY',    unlockText: 'Your hits knock enemies back harder.' },
  { percId: 'N08', file: '16_just_the_tip',      display: 'JUST THE TIP',      category: 'PROJECTILE', unlockText: 'Shots pierce through +1 enemy.' },
  { percId: 'N12', file: '17_double_tap',        display: 'DOUBLE TAP',        category: 'PROJECTILE', unlockText: 'Shots bounce to +1 extra enemy.' },
  { percId: 'T14', file: '18_happy_ending',      display: 'HAPPY ENDING',      category: 'UTILITY',    unlockText: 'Better luck. Better rolls. Better everything.' },
  { percId: 'N11', file: '19_daddy_issues',      display: 'DADDY ISSUES',      category: 'DAMAGE',     unlockText: 'Deal bonus damage to elites and bosses.' },
  { percId: 'T09', file: '20_premature_fire',    display: 'PREMATURE FIRE',    category: 'PROJECTILE', unlockText: 'Your projectiles travel faster.' },
  { percId: 'T10', file: '21_morning_wood',      display: 'MORNING WOOD',      category: 'SURVIVAL',   unlockText: 'Regenerate HP every second.' },
  { percId: 'N14', file: '22_no_contact',        display: 'NO CONTACT',        category: 'SURVIVAL',   unlockText: 'Chance to ignore contact damage entirely.' },
  // BETA 22.90 — was LAST PUMP (Debt/gold). Dylan: new name + new effect;
  // gold is dead. Art file stays 23_last_pump (the baked frames keep it).
  { percId: 'N10', file: '23_last_pump',         display: 'HEART THROB',       category: 'SURVIVAL',   unlockText: 'When your health drops critical, release a knockback shockwave.' },
  { percId: 'T13', file: '24_girth',             display: 'GIRTH',             category: 'DAMAGE',     unlockText: 'Bigger attacks. Bigger area. Bigger everything.' },
  { percId: 'N15', file: '25_money_shot',        display: 'MONEY SHOT',        category: 'PROJECTILE', unlockText: 'Fire +1 projectile with every shot.' },
  { percId: 'N07', file: '26_overstuffed',       display: 'OVERSTUFFED',       category: 'SURVIVAL',   unlockText: 'Healing can push HP past your max.' },
];

export const CARD_FAMILY_BY_PERC: Record<string, PerkCardFamily> =
  Object.fromEntries(PERK_CARD_FAMILIES.map(f => [f.percId, f]));

/** Percs with no card family — never offered on the level-up screen.
 *  N13 Not Xanax (slow-on-hit) has no card art; retired from the pool. */
export const LEVELUP_EXCLUDED_PERC_IDS: ReadonlySet<string> = new Set(['N13']);

// ── 4-TIER CARD RARITY (level-up cards only; items/chests stay 3-tier) ──────
// Base weights per Dylan's spec: Common 60 / Rare 25 / Epic 12 / Legendary 3.
// Data-driven: tune HERE, not in code. Luck shifts weight out of common into
// the higher tiers using the same luckBonus curve the old roller used
// (saturates at luck 250 — see BETA 20.77 note in peanutPercSystem).
export const CARD_RARITY_WEIGHTS: Record<'common' | 'rare' | 'epic' | 'legendary', number> = {
  common: 60, rare: 25, epic: 12, legendary: 3,
};
/** Per-point-of-luckBonus weight shift into each tier (luckBonus caps at 100). */
export const CARD_RARITY_LUCK_SHIFT: Record<'rare' | 'epic' | 'legendary', number> = {
  rare: 0.10, epic: 0.13, legendary: 0.17,
};

export function rollCardRarity(luck: number = 0): UpgradeRarity {
  const luckBonus = Math.min(Math.max(0, luck) * 0.4, 100);
  const wLegendary = CARD_RARITY_WEIGHTS.legendary + luckBonus * CARD_RARITY_LUCK_SHIFT.legendary;
  const wEpic = CARD_RARITY_WEIGHTS.epic + luckBonus * CARD_RARITY_LUCK_SHIFT.epic;
  const wRare = CARD_RARITY_WEIGHTS.rare + luckBonus * CARD_RARITY_LUCK_SHIFT.rare;
  const wCommon = Math.max(5, CARD_RARITY_WEIGHTS.common - luckBonus * (CARD_RARITY_LUCK_SHIFT.rare + CARD_RARITY_LUCK_SHIFT.epic + CARD_RARITY_LUCK_SHIFT.legendary));
  const total = wCommon + wRare + wEpic + wLegendary;
  const roll = rollRng() * total; // seeded — reproducible runs
  if (roll < wLegendary) return 'legendary';
  if (roll < wLegendary + wEpic) return 'epic';
  if (roll < wLegendary + wEpic + wRare) return 'rare';
  return 'common';
}

// ── RARITY → MAGNITUDE ──────────────────────────────────────────────────────
// Dylan's spec pattern (5/10/15/25) = ×1 / ×2 / ×3 / ×5 off the family's base.
// Jitter windows (shared rollBuffJitter: 0.85-1.15 common, 0.90-1.15 rest)
// keep tiers strictly ordered: common max 1.15 < rare min 1.80 < rare max 2.30
// < epic min 2.70 < epic max 3.45 < legendary min 4.50. Same family → rarity
// always orders the number.
export const CARD_RARITY_MULT: Record<UpgradeRarity, number> = {
  common: 1, uncommon: 1, rare: 2, epic: 3, legendary: 5,
};

/** Integer-step families (pierce/bounce/multishot): "+1" reads as +1, never
 *  +1.7. Rarity upgrades the STEP instead of multiplying a fraction. */
export const CARD_INT_STEPS: Record<string, Record<UpgradeRarity, number>> = {
  N08: { common: 1, uncommon: 1, rare: 1, epic: 2, legendary: 3 },  // JUST THE TIP (pierce)
  N12: { common: 1, uncommon: 1, rare: 1, epic: 2, legendary: 3 },  // DOUBLE TAP (bounce)
  N15: { common: 1, uncommon: 1, rare: 1, epic: 1, legendary: 2 },  // MONEY SHOT (multishot — deliberately stingier)
};

/** One-buff card bonus: base × cardRarityMult × jitter × luckValueMult.
 *  Integer families return whole steps from CARD_INT_STEPS instead. */
export function calculateCardBonus(perc: PeanutPerc, rarity: UpgradeRarity, playerLuck: number = 0): number {
  const intSteps = CARD_INT_STEPS[perc.id];
  if (intSteps) return intSteps[rarity] ?? 1;
  const jitter = rollBuffJitter(rarity, playerLuck);
  const raw = perc.bonusPerLevel * CARD_RARITY_MULT[rarity] * jitter * luckValueMult(playerLuck);
  return parseFloat(raw.toFixed(4));
}

// ── CARD VISUAL LANGUAGE ────────────────────────────────────────────────────
// Rarity text sits ABOVE the card (never inside a card box) in the tier color;
// the backlight glow behind the card speaks the same color. Legendary sparkles.
export const CARD_RARITY_STYLE: Record<string, { label: string; text: string; glow: string; sparkle: boolean }> = {
  common:    { label: 'COMMON',    text: '#f2eee2', glow: '242,238,226', sparkle: false },
  uncommon:  { label: 'COMMON',    text: '#f2eee2', glow: '242,238,226', sparkle: false },
  rare:      { label: 'RARE',      text: '#b15cff', glow: '177,92,255',  sparkle: false },
  epic:      { label: 'EPIC',      text: '#ff3b30', glow: '255,59,48',   sparkle: false },
  legendary: { label: 'LEGENDARY', text: '#ffd700', glow: '255,215,0',   sparkle: true },
};

const _CARD_TIER_DIR: Record<string, string> = {
  common: 'common', uncommon: 'common', rare: 'rare', epic: 'epic', legendary: 'legendary',
};

/** Baked card art for a family at a rarity. */
export function cardArtPath(percId: string, rarity: string): string | null {
  const fam = CARD_FAMILY_BY_PERC[percId];
  if (!fam) return null;
  return `/perk-cards/${_CARD_TIER_DIR[rarity] ?? 'common'}/${fam.file}.webp`;
}

/** Small art-only icon (window cutout) for HUD slots / pause menu. */
export function cardIconPath(percId: string): string | null {
  const fam = CARD_FAMILY_BY_PERC[percId];
  if (!fam) return null;
  return `/perk-cards/icons/${fam.file}.webp`;
}

/** Empty tier shells (weapon upgrade cards ride the same presentation). */
export function cardShellPath(rarity: string): string {
  return `/perk-cards/shells/${_CARD_TIER_DIR[rarity] ?? 'common'}_shell.webp`;
}

/** BETA 22.89 — weapon sprite BAKED into the shell (scripts/bake-weapon-cards.py).
 *  Live-drawn weapon sprites vanished during the exit/fly beat (the snapshot
 *  only carries one art url); the baked card is solid through the whole
 *  animation. Modded weapons (kitted combo icons) still live-draw — the bake
 *  only covers each weapon's base sprite. */
export function weaponCardArtPath(weaponId: string, rarity: string): string {
  return `/perk-cards/weapons/${_CARD_TIER_DIR[rarity] ?? 'common'}/${weaponId}.webp`;
}

// ── PER-TIER TEXT BOXES (BETA 22.87 r8 — Dylan's calibrator model) ──────────
// Every text/art zone is a plain RECT in card-fraction space [x0,y0,x1,y1].
// The renderer centers text inside the rect and fits the font to BOTH the
// rect's width and height. Dylan calibrates these visually with the DevPanel
// CARD CAL tool (drag/resize the boxes on all 4 tiers → SAVE); a saved
// override in localStorage wins over these defaults until it's baked in here.
//   name = top long plate · lv = right LV badge · win = art window
//   eff  = bottom effect plate · cat = bottom category plate
export type ZoneRect = [number, number, number, number];   // x0,y0,x1,y1
export interface CardTierBoxes {
  name: ZoneRect; lv: ZoneRect; win: ZoneRect; eff: ZoneRect; cat: ZoneRect;
}
export const CARD_TIER_BOXES: Record<string, CardTierBoxes> = {
  // Seeded from the r7 per-tier zone tables (identical rendered output).
  common: {
    name: [0.140, 0.084, 0.860, 0.159], lv: [0.750, 0.213, 0.940, 0.255],
    win: [0.090, 0.203, 0.918, 0.715],
    eff: [0.125, 0.744, 0.875, 0.840], cat: [0.170, 0.878, 0.830, 0.930],
  },
  uncommon: {
    name: [0.140, 0.084, 0.860, 0.159], lv: [0.750, 0.213, 0.940, 0.255],
    win: [0.090, 0.203, 0.918, 0.715],
    eff: [0.125, 0.744, 0.875, 0.840], cat: [0.170, 0.878, 0.830, 0.930],
  },
  rare: {
    name: [0.135, 0.082, 0.865, 0.157], lv: [0.732, 0.238, 0.922, 0.280],
    win: [0.064, 0.224, 0.935, 0.700],
    eff: [0.120, 0.735, 0.880, 0.831], cat: [0.170, 0.877, 0.830, 0.929],
  },
  epic: {
    name: [0.135, 0.083, 0.865, 0.158], lv: [0.723, 0.231, 0.913, 0.273],
    win: [0.068, 0.218, 0.929, 0.706],
    eff: [0.120, 0.739, 0.880, 0.835], cat: [0.170, 0.877, 0.830, 0.929],
  },
  legendary: {
    name: [0.140, 0.084, 0.860, 0.159], lv: [0.740, 0.240, 0.930, 0.282],
    win: [0.048, 0.217, 0.950, 0.686],
    eff: [0.120, 0.732, 0.880, 0.828], cat: [0.170, 0.878, 0.830, 0.930],
  },
};

// ── Calibrator override (dev-only artifact; only exists after Dylan SAVEs
// in the CARD CAL tool). Parsed once, updated live via applyCardCalOverride. ─
export const CARDCAL_LS_KEY = 'cardcal_boxes_v1';
let _cardcalOverride: Record<string, CardTierBoxes> | null | undefined;

function _readOverride(): Record<string, CardTierBoxes> | null {
  try {
    const raw = localStorage.getItem(CARDCAL_LS_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return parsed && parsed.version === 1 && parsed.boxes ? parsed.boxes : null;
  } catch { return null; }
}

/** The renderer's single entry point — override-aware per-tier boxes. */
export function getCardTierBoxes(rarity: string): CardTierBoxes {
  if (_cardcalOverride === undefined) _cardcalOverride = _readOverride();
  const key = rarity === 'uncommon' ? 'common' : rarity;
  return _cardcalOverride?.[key]
    ?? CARD_TIER_BOXES[rarity] ?? CARD_TIER_BOXES.common;
}

/** CARD CAL tool SAVE → persist + apply live (next frame picks it up). */
export function applyCardCalOverride(boxes: Record<string, CardTierBoxes>): void {
  _cardcalOverride = boxes;
  try { localStorage.setItem(CARDCAL_LS_KEY, JSON.stringify({ version: 1, boxes })); } catch { /* private mode */ }
}

/** CARD CAL tool RESET ALL → back to the shipped defaults. */
export function clearCardCalOverride(): void {
  _cardcalOverride = null;
  try { localStorage.removeItem(CARDCAL_LS_KEY); } catch { /* private mode */ }
}

// ── MULTIPLAYER MODE (ready, dormant — no co-op exists in the engine yet) ───
// Dylan's preferred direction: same 3 cards, NO pause, stacked on the right,
// 5s countdown, weighted auto-pick on expiry. Flip `enabled` when co-op lands.
export const MULTI_LEVELUP = {
  enabled: false,
  countdownSec: 5,
  /** auto-pick weighting: highest rarity wins, tie → first card */
  autoPickHighestRarity: true,
} as const;

export const CARD_RARITY_ORDER: Record<string, number> = {
  common: 0, uncommon: 0, rare: 1, epic: 2, legendary: 3,
};
